import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './style.module.css';

const FitnessGoalStep = ({ onNext, onBack, selectedGoal: initialGoal, stepData }) => {
  // Initialize state - don't pre-fill if initialGoal is the hubspotSkipValue
  const [selectedGoal, setSelectedGoal] = useState(() => {
    // If initialGoal is the hubspotSkipValue, return empty string
    if (initialGoal === stepData?.hubspotSkipValue) {
      return '';
    }
    return initialGoal || '';
  });

  // Get fitness goals from API
  const fitnessGoals =
    stepData?.options
      ?.map(option => ({
        id: option.value || option.label, // Use value as ID, fallback to label
        label: option.label,
        value: option.value,
        displayOrder: option.displayOrder,
      }))
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)) || [];

  const handleGoalSelect = goalId => {
    setSelectedGoal(goalId);
  };

  const handleNextClick = () => {
    if (onNext && selectedGoal) {
      // Find the selected goal object to get its value
      const selectedGoalObject = fitnessGoals.find(goal => goal.id === selectedGoal);

      const valueToSend = selectedGoalObject?.value || selectedGoal;
      onNext(valueToSend);
    }
  };

  const handleSkipClick = () => {
    if (onNext) {
      // Clear current selection when skipping
      setSelectedGoal('');
      // Use hubspotSkipValue from API if available
      onNext(stepData?.hubspotSkipValue);
    }
  };

  // Validation function based on API minInput/maxInput
  const isNextButtonEnabled = () => {
    const minInput = stepData?.validations?.minInput;
    const maxInput = stepData?.validations?.maxInput;

    // Check if user has selected something (selectedGoal is a string/ID)
    const hasSelection = selectedGoal && selectedGoal.trim() !== '';

    // For single selection (fitness goal), minInput of 1 means at least one selection required
    if (minInput === 1) {
      return hasSelection;
    }

    // If minInput is 0, allow empty selection
    return true;
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerContainer}>
        <h2 className={styles.header}>{stepData?.title}</h2>
        {stepData?.subtitle && <p className={styles.subHeader}>{stepData.subtitle}</p>}
      </div>

      <div className={styles.goalsContainer}>
        {fitnessGoals.map((goal, index) => (
          <div
            key={goal.id}
            className={`${styles.goalCard} ${
              selectedGoal === goal.id ? styles.selectedCard : ''
            }`}
            onClick={() => handleGoalSelect(goal.id)}
          >
            <span className={styles.goalText}>{goal.label}</span>
          </div>
        ))}
      </div>

      <div className={styles.buttonsContainer}>
        <div className={styles.buttonGroup}>
          <button type="button" className={styles.skipButton} onClick={handleSkipClick}>
            Skip
          </button>
          <button
            type="button"
            className={styles.nextButton}
            disabled={!isNextButtonEnabled()}
            onClick={handleNextClick}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

FitnessGoalStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  selectedGoal: PropTypes.string,
  stepData: PropTypes.object,
};

FitnessGoalStep.defaultProps = {
  onBack: () => {},
  selectedGoal: '',
};

export default FitnessGoalStep;
